import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Search, RefreshCw, Loader2, Download, FileText, ChevronDown, ChevronUp, Calendar, CheckCircle2, Clock, ClipboardList } from 'lucide-react'
import { reportCardsApi, studentsApi, classesApi } from '../api/services.js'
import Avatar from '../components/Avatar.jsx'
import PageHeader from '../components/PageHeader.jsx'
import toast from 'react-hot-toast'

const SESSION_STATUS_COLORS = {
  active:    '#0891b2',
  completed: '#15803d',
  upcoming:  '#d97706',
  draft:     '#6b7280',
  published: '#16a34a',
  ongoing:   '#0f766e',
}

const getStudentName = (student) => (
  student?.name || `${student?.firstName || ''} ${student?.lastName || ''}`.trim() || '—'
)

const getClassLabel = (student) => {
  const classInfo = student?.classId
  if (!classInfo) return student?.class?.name || student?.className || ''
  return classInfo.name ? (classInfo.section ? `${classInfo.name} — ${classInfo.section}` : classInfo.name) : ''
}

export default function ReportCardsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [students, setStudents] = useState([])
  const [classes, setClasses] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filterClass, setFilterClass] = useState('')
  const [expandedStudent, setExpandedStudent] = useState(null)
  const [sessions, setSessions] = useState({})
  const [loadingSessions, setLoadingSessions] = useState({})
  const [downloading, setDownloading] = useState({})
  const selectedStudentId = searchParams.get('studentId') || ''

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        const [studentsResponse, classesResponse] = await Promise.allSettled([
          studentsApi.getAll(filterClass ? { classId: filterClass } : {}),
          classesApi.getAll(),
        ])

        if (studentsResponse.status === 'fulfilled') {
          setStudents(Array.isArray(studentsResponse.value) ? studentsResponse.value : studentsResponse.value?.users ?? [])
        }

        if (classesResponse.status === 'fulfilled') {
          setClasses(Array.isArray(classesResponse.value) ? classesResponse.value : classesResponse.value?.classes ?? [])
        }
      } catch {
        toast.error('Failed to load students')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [filterClass])

  const loadStudentSessions = async (studentId) => {
    if (!studentId || sessions[studentId] || loadingSessions[studentId]) return

    try {
      setLoadingSessions((current) => ({ ...current, [studentId]: true }))
      const data = await reportCardsApi.getSessions(studentId)
      setSessions((current) => ({ ...current, [studentId]: Array.isArray(data) ? data : data?.sessions ?? [] }))
    } catch {
      toast.error('Failed to load sessions')
      setSessions((current) => ({ ...current, [studentId]: [] }))
    } finally {
      setLoadingSessions((current) => ({ ...current, [studentId]: false }))
    }
  }

  useEffect(() => {
    if (!selectedStudentId) return
    setExpandedStudent(selectedStudentId)
    loadStudentSessions(selectedStudentId)
  }, [selectedStudentId])

  const toggleStudent = async (student) => {
    const studentId = student?._id || student?.id
    if (!studentId) return

    if (expandedStudent === studentId) {
      setExpandedStudent(null)
      return
    }

    setExpandedStudent(studentId)
    await loadStudentSessions(studentId)
  }

  const handleDownload = async (studentId, sessionId) => {
    const key = `${studentId}-${sessionId}`

    try {
      setDownloading((current) => ({ ...current, [key]: true }))
      const response = await reportCardsApi.downloadCard(studentId, sessionId)
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `report-card-${studentId}-${sessionId}.pdf`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
      toast.success('Downloaded')
    } catch {
      toast.error('Download failed')
    } finally {
      setDownloading((current) => ({ ...current, [key]: false }))
    }
  }

  const filteredStudents = useMemo(() => (
    students.filter((student) => getStudentName(student).toLowerCase().includes(search.toLowerCase()))
  ), [search, students])

  const visibleStudents = useMemo(() => (
    selectedStudentId
      ? filteredStudents.filter((student) => String(student._id || student.id) === String(selectedStudentId))
      : filteredStudents
  ), [filteredStudents, selectedStudentId])

  const selectedStudent = useMemo(() => (
    students.find((student) => String(student._id || student.id) === String(selectedStudentId)) || null
  ), [selectedStudentId, students])

  return (
    <div className="space-y-5">
      <PageHeader
        title="Report Cards"
        subtitle={selectedStudent
          ? `Review ${getStudentName(selectedStudent)}'s sessions, open the detailed report view, and then drill into marksheets just like the RN admin flow.`
          : 'Browse student report card sessions, inspect the detailed result breakdown, and download the published PDFs.'}
        chips={[
          loading ? 'Loading students...' : `${visibleStudents.length} visible`,
          selectedStudent ? getStudentName(selectedStudent) : 'All students',
          filterClass ? 'Class filter applied' : 'All class sections',
        ]}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {selectedStudentId && (
              <button type="button" onClick={() => setSearchParams({})} className="btn-secondary">
                Show All Students
              </button>
            )}
            <button className="btn-secondary p-2" onClick={() => window.location.reload()} title="Refresh report cards">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        }
      />

      <div className="card flex flex-wrap items-center gap-3 p-4">
        <div className="flex min-w-[180px] flex-1 items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
          <Search className="h-4 w-4 flex-shrink-0 text-gray-400" />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search student..."
            className="w-full bg-transparent text-sm outline-none"
          />
        </div>
        <select className="input-field" value={filterClass} onChange={(event) => setFilterClass(event.target.value)}>
          <option value="">All Class Sections</option>
          {classes.map((classItem) => (
            <option key={classItem._id || classItem.id} value={classItem._id || classItem.id}>
              {classItem.name}{classItem.section ? ` — ${classItem.section}` : ''}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-2">
        {loading ? [...Array(6)].map((_, index) => (
          <div key={index} className="card flex animate-pulse items-center gap-4 p-4">
            <div className="h-9 w-9 rounded-full bg-gray-100" />
            <div className="flex-1 space-y-2">
              <div className="h-4 w-1/3 rounded bg-gray-100" />
              <div className="h-3 w-1/4 rounded bg-gray-100" />
            </div>
          </div>
        )) : visibleStudents.length === 0 ? (
          <div className="py-12 text-center text-gray-400">
            <FileText className="mx-auto mb-3 h-10 w-10 text-gray-200" />
            <p className="text-sm">
              {selectedStudentId ? 'That student could not be found in the current list.' : 'No students found.'}
            </p>
          </div>
        ) : visibleStudents.map((student) => {
          const studentId = student._id || student.id
          const isExpanded = expandedStudent === studentId

          return (
            <div key={studentId} className="card overflow-hidden">
              <div
                className="flex cursor-pointer items-center gap-3 p-4 transition-colors hover:bg-gray-50"
                onClick={() => toggleStudent(student)}
              >
                <Avatar src={student.avatar} name={getStudentName(student)} size="md" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-900">{getStudentName(student)}</p>
                  <p className="text-xs text-gray-400">
                    {getClassLabel(student)} {student.admissionNo ? `· ${student.admissionNo}` : ''}
                  </p>
                </div>
                {isExpanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
              </div>

              {isExpanded && (
                <div className="border-t border-gray-100 bg-gray-50 p-4">
                  {loadingSessions[studentId] ? (
                    <div className="flex justify-center py-4">
                      <Loader2 className="h-5 w-5 animate-spin text-gray-300" />
                    </div>
                  ) : (sessions[studentId] || []).length === 0 ? (
                    <p className="py-3 text-center text-sm text-gray-400">No report cards available.</p>
                  ) : (
                    <div className="space-y-2">
                      {(sessions[studentId] || []).map((session, index) => {
                        const sessionId = session._id || session.id || session.sessionId
                        const downloadKey = `${studentId}-${sessionId}`
                        const statusKey = session.status || 'draft'
                        const accentColor = SESSION_STATUS_COLORS[statusKey] || '#6b7280'
                        const isReady = Boolean(session.reportReady)
                        const sessionName = session.name || session.sessionName || `Session ${index + 1}`
                        const startDate = session.startDate
                          ? new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(session.startDate))
                          : null
                        const endDate = session.endDate
                          ? new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(session.endDate))
                          : null

                        return (
                          <div key={sessionId || index} className="flex rounded-xl border border-gray-100 bg-white overflow-hidden">
                            {/* Accent bar */}
                            <div className="w-1 flex-shrink-0" style={{ backgroundColor: accentColor }} />
                            <div className="flex-1 p-3 space-y-2">
                              {/* Top row */}
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                  <p className="text-sm font-bold text-gray-900">{sessionName}</p>
                                  {session.academicYear && (
                                    <p className="text-xs font-medium text-gray-500 mt-0.5">{session.academicYear}</p>
                                  )}
                                </div>
                              </div>

                              {/* Date range */}
                              {(startDate || endDate) && (
                                <div className="flex items-center gap-1.5">
                                  <Calendar className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                  <span className="text-xs text-gray-400">
                                    {startDate || '—'} – {endDate || '—'}
                                  </span>
                                </div>
                              )}

                              {/* Badges */}
                              <div className="flex flex-wrap items-center gap-1.5">
                                {/* Status badge with dot */}
                                <span
                                  className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium border"
                                  style={{
                                    backgroundColor: `${accentColor}15`,
                                    borderColor: `${accentColor}40`,
                                    color: accentColor,
                                  }}
                                >
                                  <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: accentColor }} />
                                  {statusKey}
                                </span>

                                {/* Exam count */}
                                {session.examsCount != null && (
                                  <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium bg-primary-50 text-primary-700 border border-primary-100">
                                    <ClipboardList className="w-3 h-3" />
                                    {session.examsCount} exam{session.examsCount !== 1 ? 's' : ''}
                                  </span>
                                )}

                                {/* Ready / Not published */}
                                <span
                                  className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium border"
                                  style={{
                                    backgroundColor: isReady ? '#dcfce7' : '#fef3c7',
                                    borderColor: isReady ? '#86efac' : '#fcd34d',
                                    color: isReady ? '#15803d' : '#d97706',
                                  }}
                                >
                                  {isReady
                                    ? <CheckCircle2 className="w-3 h-3" />
                                    : <Clock className="w-3 h-3" />}
                                  {isReady ? 'Results ready' : 'Not published'}
                                </span>
                              </div>

                              {/* Actions */}
                              <div className="flex flex-wrap items-center gap-2 pt-1">
                                {sessionId && (
                                  <Link
                                    to={`/report-cards/${studentId}/${sessionId}`}
                                    className="btn-secondary inline-flex items-center gap-1.5 px-3 py-1.5 text-xs"
                                  >
                                    <FileText className="h-3 w-3" /> View Details
                                  </Link>
                                )}
                                <button
                                  onClick={() => handleDownload(studentId, sessionId)}
                                  disabled={downloading[downloadKey]}
                                  className="btn-secondary flex items-center gap-1.5 px-3 py-1.5 text-xs disabled:opacity-50"
                                >
                                  {downloading[downloadKey] ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
                                  Download
                                </button>
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
